import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Printer,
  PlusSquare,
  BarChart3,
  Info,
  Settings,
  MapPin,
  Package,
  CheckCircle2,
  AlertCircle,
  Search,
  X,
  ArrowDownToLine,
  RotateCcw,
  Edit3
} from 'lucide-react';
import { useSwipeAction } from '../../../../hooks/useSwipeAction';
import {
  StcIntRelOrderDetailsDto,
  StcIntRelOrderLineDto,
  CompletePutAwayLineResponse,
  ItemBinsUpdateInfo
} from '../../../../types/putaway';
import { putAwayService } from '../../../../services/putAwayService';
import { logger } from '../../../../services/loggingService';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { parseGS1Barcode } from '../../../../utils/gs1Parser';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { settingsService } from '../../../../services/settingsService';
import { CapacityUpdateModal } from '../../../shared/CapacityUpdateModal';
import { itemBinSizeService } from '../../../../services/itemBinSizeService';
import { stcBinService } from '../../../../services/stcBinService';
import { putAwayDiagnosticsService } from '../../../../services/putAwayDiagnosticsService';
import { ExtendShelfModal } from '../../shared/ExtendShelfModal';

// Skann-innstillinger for vareplassering. Hentes via UnifiedSettings
// (putaway.barCodeConfirmItem / putaway.barCodeConfirmLocation) sa de er
// uavhengige av plukk-settingene pa UserProfile (BarCodeConfirmItem/Location).
type PutAwayScanProfile = {
  barCodeConfirmItem: boolean;
  barCodeConfirmLocation: boolean;
};

interface ExtendedPutAwayLine extends StcIntRelOrderLineDto {
  isPartiallyMoved?: boolean;
  isFullyMoved?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  itemDescription?: string;
  location?: string;
}

interface ModernPutAwayDetailsProps {
  stcOrderNo: number;
  onBack: () => void;
}

// Scanning step states
type ScanningStep = 'item' | 'location' | 'quantity' | 'completed';

interface ScanningState {
  step: ScanningStep;
  scannedItem: string | null;
  expectedLocation: string | null;
  scannedLocation: string | null;
  expectedQuantity: number | null;
  tempQuantity: number | null;
  currentLineIndex: number | null;
}

/** Wrapper that adds right-swipe-to-bottom gesture on a put-away line */
const SwipeablePutAwayLine: React.FC<{
  stcLineNo: number;
  enabled: boolean;
  isDeferred: boolean;
  onSwipeToBottom: (stcLineNo: number) => void;
  children: React.ReactNode;
}> = ({ stcLineNo, enabled, isDeferred, onSwipeToBottom, children }) => {
  const { handlers, offset, isSwiping } = useSwipeAction({
    onSwipeRight: () => onSwipeToBottom(stcLineNo),
    threshold: 80,
    enabled,
  });

  return (
    <div className="relative overflow-hidden">
      {/* Background revealed during swipe */}
      {offset > 0 && (
        <div className="absolute inset-0 flex items-center bg-orange-100 text-orange-700 px-4">
          <ArrowDownToLine className="w-5 h-5 mr-2" />
          <span className="text-sm font-medium">Flytt til bunn</span>
        </div>
      )}
      {/* Swipeable content */}
      <div
        {...handlers}
        className="bg-white"
        style={{
          transform: offset > 0 ? `translateX(${offset}px)` : undefined,
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out',
          touchAction: 'pan-y',
          position: 'relative',
          zIndex: 1,
        }}
        onClickCapture={(e) => {
          // Prevent click from firing when swipe just completed
          if (isSwiping) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const ModernPutAwayDetails: React.FC<ModernPutAwayDetailsProps> = ({
  stcOrderNo,
  onBack
}) => {
  const { t } = useTranslation();
  const [putAwayDetails, setPutAwayDetails] = useState<StcIntRelOrderDetailsDto | null>(null);
  const [putAwayLines, setPutAwayLines] = useState<ExtendedPutAwayLine[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User profile for scanning settings
  const [userProfile, setUserProfile] = useState<PutAwayScanProfile | null>(null);

  // Scanner state
  const [scanInput, setScanInput] = useState('');
  const scannerRef = useRef<HTMLInputElement>(null);

  // Scanning workflow state
  const [scanningState, setScanningState] = useState<ScanningState>({
    step: 'item',
    scannedItem: null,
    expectedLocation: null,
    scannedLocation: null,
    expectedQuantity: null,
    tempQuantity: null,
    currentLineIndex: null
  });

  // Processing state to prevent race conditions
  const [processingUpdates, setProcessingUpdates] = useState<Set<number>>(new Set());

  // Editing states
  const [editingFromLocation, setEditingFromLocation] = useState<number | null>(null);
  const [newFromLocation, setNewFromLocation] = useState('');
  const [editingToLocation, setEditingToLocation] = useState<number | null>(null);
  const [newToLocation, setNewToLocation] = useState('');
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [newQuantity, setNewQuantity] = useState('');

  // Extend shelf modal state — lar operatør legge til ny lokasjon på hyllen
  const [showExtendShelfModal, setShowExtendShelfModal] = useState(false);

  // Bulk completion modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkLocationSearch, setShowBulkLocationSearch] = useState(false);
  const [bulkToBinCode, setBulkToBinCode] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Bytt-lokasjon modal: lar operatør overstyre foreslått toBinCode for aktiv linje
  // (relevant når operatør ikke skanner lokasjon, eller ønsker å avvike fra forslaget)
  const [showChangeLocationSearch, setShowChangeLocationSearch] = useState(false);
  const [isChangingLocation, setIsChangingLocation] = useState(false);

  // Capacity update modal state (triggered after split/partial placement)
  const [capacityModalData, setCapacityModalData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    binSizeNo: number;
    currentQuantity: number;
    currentMaxQuantity?: number;
    existingItemBinSizeId?: number | null;
    existingPutAwayPriorityNo?: number;
    // For re-suggesting bin on the rest-line after capacity update
    newLineStcLineNo?: number;
    warehouseID?: string;
    remainingQuantity?: number;
    binGrpNo?: number;
  } | null>(null);

  // Ref to avoid stale closure in onSaved callback
  const capacityModalDataRef = useRef(capacityModalData);
  capacityModalDataRef.current = capacityModalData;

  // Deferred lines (swiped to bottom)
  const [deferredLineNos, setDeferredLineNos] = useState<Set<number>>(new Set());
  const [originalLineOrder, setOriginalLineOrder] = useState<number[]>([]);

  const moveLineToBottom = useCallback((stcLineNo: number) => {
    setPutAwayLines(prev => {
      const idx = prev.findIndex(l => l.stcLineNo === stcLineNo);
      if (idx === -1) return prev;
      const line = prev[idx];
      const newLines = [...prev.slice(0, idx), ...prev.slice(idx + 1), line];
      // Recalculate active line index for reordered list
      const firstIncomplete = newLines.findIndex(l => (l.quantityMoved || 0) === 0);
      setActiveLineIndex(firstIncomplete !== -1 ? firstIncomplete : null);
      setCurrentLineIndex(null);
      return newLines;
    });
    setDeferredLineNos(prev => new Set(prev).add(stcLineNo));
  }, []);

  const resetLineOrder = useCallback(() => {
    if (originalLineOrder.length === 0) return;
    setPutAwayLines(prev => {
      const lineMap = new Map(prev.map(l => [l.stcLineNo, l]));
      const reordered = originalLineOrder
        .map(no => lineMap.get(no))
        .filter((l): l is ExtendedPutAwayLine => l !== undefined);
      // Add any lines not in original order (e.g. split lines created after load)
      const originalSet = new Set(originalLineOrder);
      const extras = prev.filter(l => !originalSet.has(l.stcLineNo));
      return [...reordered, ...extras];
    });
    setDeferredLineNos(new Set());
    // Recalculate active line
    setTimeout(() => {
      setPutAwayLines(prev => {
        const firstIncomplete = prev.findIndex(l => (l.quantityMoved || 0) === 0);
        setActiveLineIndex(firstIncomplete !== -1 ? firstIncomplete : null);
        return prev;
      });
    }, 0);
  }, [originalLineOrder]);

  // Detect if any modal is open to disable global scanning
  const isAnyModalOpen = showBulkModal || showChangeLocationSearch;

  // Check if global barcode scanning is enabled via system setting
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Global barcode scanner - processes scans without requiring input focus
  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      // In location-scan-only mode, route directly to location handler
      if (isLocationScanOnlyMode && activeLine) {
        const cleanedBarcode = barcode.replace(/[~§]+$/g, '').trim();
        handleLocationOnlyScan(cleanedBarcode);
        return;
      }
      processGlobalBarcode(barcode);
    }
  });

  useEffect(() => {
    logger.info('ModernPutAwayDetails', 'Component mounted, loading scan settings and put-away details', { stcOrderNo });
    const init = async () => {
      // Hent skann-settinger for vareplassering fra UnifiedSettings.
      // GetEffectiveSetting leser i rekkefolge: ProfileSetting -> SystemSetting -> DefaultValue.
      let profile: PutAwayScanProfile | null = null;
      try {
        const [itemSetting, locationSetting] = await Promise.all([
          settingsService.getEffectiveSetting('putaway.barCodeConfirmItem'),
          settingsService.getEffectiveSetting('putaway.barCodeConfirmLocation')
        ]);
        // Default true matcher SettingDefinition.DefaultValue. Hvis settingen
        // ikke finnes (f.eks. master-migrasjon ikke kjort), faller vi tilbake
        // til samme oppforsel som tidligere (krev skanning).
        const resolveBool = (v: string | undefined, fallback: boolean): boolean =>
          v === undefined ? fallback : v === 'true' || v === '1';
        profile = {
          barCodeConfirmItem: resolveBool(itemSetting?.currentValue, true),
          barCodeConfirmLocation: resolveBool(locationSetting?.currentValue, true)
        };
        setUserProfile(profile);
        logger.info('ModernPutAwayDetails', 'Put-away scan settings loaded', {
          barCodeConfirmLocation: profile.barCodeConfirmLocation,
          barCodeConfirmItem: profile.barCodeConfirmItem,
          itemSource: itemSetting?.effectiveSource,
          locationSource: locationSetting?.effectiveSource
        });
      } catch (error) {
        logger.warn('ModernPutAwayDetails', 'Could not load put-away scan settings, using defaults (true/true)', { error });
        profile = { barCodeConfirmItem: true, barCodeConfirmLocation: true };
        setUserProfile(profile);
      }
      // Then load put-away details with profile
      await loadPutAwayDetails(profile);

      // Set initial scanning step based on profile
      const reqLocation = profile?.barCodeConfirmLocation ?? false;
      const reqItem = profile?.barCodeConfirmItem ?? false;
      if (reqLocation && !reqItem) {
        setScanningState(prev => ({ ...prev, step: 'location' }));
      }
    };
    init();
  }, [stcOrderNo]);

  // Handle Escape key to close bulk modal
  useEffect(() => {
    if (!showBulkModal) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBulkProcessing) {
        setShowBulkModal(false);
        setBulkToBinCode('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showBulkModal, isBulkProcessing]);

  // Auto-focus disabled - global scanning is active
  // useEffect(() => {
  //   if (scannerRef.current) {
  //     scannerRef.current.focus();
  //     // Select all text when in quantity step for easy editing
  //     if (scanningState.step === 'quantity') {
  //       scannerRef.current.select();
  //     }
  //   }
  // }, [scanningState.step]);

  const loadPutAwayDetails = async (profileOverride?: PutAwayScanProfile | null) => {
    const startTime = Date.now();

    try {
      setLoading(true);
      setError(null);

      logger.info('ModernPutAwayDetails', 'Loading put-away details', { stcOrderNo });

      const details = await putAwayService.getPutAwayOrderDetails(stcOrderNo);
      setPutAwayDetails(details);

      // Use profile settings for scanning requirements
      const effectiveProfile = profileOverride !== undefined ? profileOverride : userProfile;
      const requireLocationScan = effectiveProfile?.barCodeConfirmLocation ?? false;
      const requireItemScan = effectiveProfile?.barCodeConfirmItem ?? false;

      // Convert to ExtendedPutAwayLine format
      const lines: ExtendedPutAwayLine[] = details.lines.map(line => ({
        ...line,
        isPartiallyMoved: (line.quantityMoved || 0) > 0 && (line.quantityMoved || 0) < line.quantity,
        isFullyMoved: (line.quantityMoved || 0) >= line.quantity,
        requireLocationScan,
        requireItemScan,
        image: Math.random() > 0.7 ? `https://via.placeholder.com/300x200/e8f5e8/2e7d32?text=${encodeURIComponent(line.itemNo || 'Item')}` : undefined,
        itemDescription: `${line.itemNo || t('common.labels.unknown')} - ${line.description1 || t('common.labels.description')}`,
        location: line.toBinCode || t('common.labels.notAssigned')
      }));
      
      setPutAwayLines(lines);
      setOriginalLineOrder(lines.map(l => l.stcLineNo));
      setDeferredLineNos(new Set());

      // Set first incomplete line as active
      const firstIncompleteIndex = lines.findIndex(line => (line.quantityMoved || 0) === 0);
      setActiveLineIndex(firstIncompleteIndex !== -1 ? firstIncompleteIndex : null);
      
      const duration = Date.now() - startTime;
      logger.info('ModernPutAwayDetails', 'Successfully loaded put-away details', {
        stcOrderNo,
        duration,
        totalLines: lines.length,
        completedLines: lines.filter(l => (l.quantityMoved || 0) > 0).length
      });
    } catch (err) {
      const duration = Date.now() - startTime;
      const error = err as Error;
      
      logger.logException('ModernPutAwayDetails', error, {
        operation: 'loadPutAwayDetails',
        stcOrderNo,
        duration
      });
      
      console.error('Error loading put-away details:', err);
      setError(t('warehouse.modules.putaway.errors.loadDetailsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const activeLine = activeLineIndex !== null ? putAwayLines[activeLineIndex] : null;
  const completedLines = putAwayLines.filter(line => (line.quantityMoved || 0) > 0).length;
  const allLinesFullyPlaced = putAwayLines.length > 0 && putAwayLines.every(line => (line.quantityMoved || 0) >= line.quantity);

  // Determine scanning mode based on profile settings
  const isLocationScanOnlyMode = activeLine?.requireLocationScan === true
    && activeLine?.requireItemScan === false;
  const isItemScanOnlyMode = activeLine?.requireItemScan === true
    && activeLine?.requireLocationScan === false;
  const isBothScanMode = activeLine?.requireLocationScan === true
    && activeLine?.requireItemScan === true;
  const isNoScanMode = !activeLine?.requireLocationScan && !activeLine?.requireItemScan;

  // Move completed line to bottom and set next active line (like Moderne Plukk)
  const moveLineToBottomAndSetNext = (completedLineIndex: number) => {
    setPutAwayLines(prevLines => {
      const updatedLines = [...prevLines];
      const completedLine = updatedLines[completedLineIndex];
      
      if (completedLine) {
        // Remove completed line from current position
        updatedLines.splice(completedLineIndex, 1);
        
        // Add to bottom with preserved state
        updatedLines.push({
          ...completedLine,
          quantityMoved: completedLine.quantityMoved,
          isPartiallyMoved: completedLine.isPartiallyMoved,
          isFullyMoved: completedLine.isFullyMoved
        });
        
        // Find next incomplete line
        const nextIncompleteIndex = updatedLines.findIndex(line => (line.quantityMoved || 0) === 0);
        setActiveLineIndex(nextIncompleteIndex !== -1 ? nextIncompleteIndex : null);
        
        // Clear current selection since active line moved
        setCurrentLineIndex(null);
        
        // Reset scanning workflow
        resetScanningWorkflow();
      }
      
      return updatedLines;
    });
  };

  const resetScanningWorkflow = () => {
    // In location-scan-only mode, start at 'location' step (skip item scan)
    const effectiveProfile = userProfile;
    const reqLocation = effectiveProfile?.barCodeConfirmLocation ?? false;
    const reqItem = effectiveProfile?.barCodeConfirmItem ?? false;
    const locationOnly = reqLocation && !reqItem;

    setScanningState({
      step: locationOnly ? 'location' : 'item',
      scannedItem: null,
      expectedLocation: null,
      scannedLocation: null,
      expectedQuantity: null,
      tempQuantity: null,
      currentLineIndex: null
    });
    setScanInput('');
  };

  // Handle line selection from list
  const handleLineSelect = (index: number) => {
    logger.logUserAction('select_putaway_line', {
      stcOrderNo,
      lineIndex: index,
      stcLineNo: putAwayLines[index]?.stcLineNo
    });
    
    setActiveLineIndex(index);
    setCurrentLineIndex(index);
    
    // Reset scanning workflow when manually selecting a line
    resetScanningWorkflow();
  };

  // Parse barcode with GS1-128 support
  const parseBarcodeWithGS1 = (scanInput: string): { itemNo: string; location: string; quantity?: number } => {
    console.log('🔍 Parsing barcode:', scanInput);

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix, or parentheses
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    // A real GS1 barcode has structure indicators, not just trailing separators from scanner
    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1Barcode(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data && fullGS1Data) {
      console.log('📊 Detected GS1-128 barcode');
      // GS1 barcode - extract GTIN and quantity if available
      const itemNo = fullGS1Data.gtin || scanInput;
      const quantity = fullGS1Data.quantity;

      console.log('📋 Extracted GS1 data - itemNo:', itemNo, 'quantity:', quantity);

      return {
        itemNo,
        location: itemNo, // Can be used for location scanning too
        quantity
      };
    }

    // Not GS1-128 or parsing failed - treat as plain barcode
    console.log('📝 Plain barcode (not GS1-128)');
    // Strip trailing scanner separators (~ and §) from plain barcodes
    const cleanedValue = scanInput.replace(/[~§]+$/g, '').trim();
    console.log('🧹 Cleaned plain barcode:', cleanedValue);

    return {
      itemNo: cleanedValue,
      location: cleanedValue
    };
  };

  // Find matching line by item number, GTIN or itemBarCode
  // Mirrors picking module pattern (usePickingData.ts findLineByItem):
  // - Case-insensitive matching
  // - Leading-zero stripping (GTINs often have padding zeros)
  // - Matches against both itemNo and itemBarCode
  const findLineByItem = (scannedItem: string): ExtendedPutAwayLine | null => {
    const scannedLower = scannedItem.toLowerCase();
    const cleanedScanned = scannedItem.replace(/^0+/, '') || scannedItem;
    const cleanedLower = cleanedScanned.toLowerCase();

    console.log('🔍 findLineByItem - scanned:', scannedItem, 'cleaned:', cleanedScanned,
      'lines:', putAwayLines.map(l => ({ itemNo: l.itemNo, itemBarCode: l.itemBarCode })));

    const matches = (line: ExtendedPutAwayLine): boolean => {
      const lineItemNo = line.itemNo?.toLowerCase();
      const lineBarCode = line.itemBarCode?.toLowerCase();

      if (lineItemNo && (lineItemNo === scannedLower || lineItemNo === cleanedLower)) return true;
      if (lineBarCode && (lineBarCode === scannedLower || lineBarCode === cleanedLower)) return true;
      return false;
    };

    // Prefer incomplete lines (not yet placed)
    const incompleteLine = putAwayLines.find(l => matches(l) && (l.quantityMoved || 0) === 0);
    if (incompleteLine) return incompleteLine;

    // Fallback to any matching line
    return putAwayLines.find(l => matches(l)) || null;
  };

  // Handle location-only scan mode: operator scans location → quantity confirmation
  const handleLocationOnlyScan = async (scannedLocation: string) => {
    if (!activeLine || activeLineIndex === null) {
      alert(t('warehouse.modules.putaway.errors.noActiveLineFound'));
      return;
    }

    logger.logUserAction('location_only_scan', {
      stcOrderNo,
      scannedLocation,
      expectedLocation: activeLine.toBinCode,
      activeLineIndex,
      itemNo: activeLine.itemNo
    });

    const expectedLocation = activeLine.toBinCode || '';

    // Check if location matches
    if (scannedLocation !== expectedLocation) {
      const confirmOverride = window.confirm(
        `${t('warehouse.modules.putaway.errors.wrongLocationScanned')}\n\n${t('warehouse.modules.putaway.labels.expected')}: ${expectedLocation}\n${t('warehouse.modules.putaway.labels.scanned')}: ${scannedLocation}\n\n${t('warehouse.modules.putaway.questions.continueAnyway')}`
      );

      if (!confirmOverride) {
        return;
      }

      // Update toBinCode in database when overridden
      try {
        const updateRequest = {
          warehouseID: activeLine.warehouseID || 'W01',
          stcOrderNo: activeLine.stcOrderNo,
          stcLineNo: activeLine.stcLineNo,
          itemNo: activeLine.itemNo,
          quantity: activeLine.quantity,
          fromBinCode: activeLine.fromBinCode,
          toBinCode: scannedLocation,
          grShipmentNo: activeLine.grShipmentNo,
          stcBoxNo: activeLine.stcBoxNo,
          lineStatus: activeLine.lineStatus,
          movedBy: activeLine.movedBy,
          itemGrp1: activeLine.itemGrp1,
          itemGrp2: activeLine.itemGrp2,
          itemGrp3: activeLine.itemGrp3,
          description1: activeLine.description1,
          resourceNo: activeLine.resourceNo,
          stcZoneID: activeLine.stcZoneID,
          transType: activeLine.transType,
          clientID: activeLine.clientID,
          quantityMoved: activeLine.quantityMoved,
          itemBarCode: activeLine.itemBarCode,
          externalSystemID: activeLine.externalSystemID,
          externalSystemStatus: activeLine.externalSystemStatus,
          resID: activeLine.resID,
          binGrpNo: activeLine.binGrpNo,
          sorting: activeLine.sorting,
          relID: activeLine.relID
        };

        await putAwayService.updatePutAwayOrderLine(stcOrderNo, activeLine.stcLineNo, updateRequest);

        // Update local state
        setPutAwayLines(prevLines =>
          prevLines.map(line =>
            line.stcLineNo === activeLine.stcLineNo
              ? { ...line, toBinCode: scannedLocation, location: scannedLocation }
              : line
          )
        );
      } catch (error) {
        logger.logException('ModernPutAwayDetails', error as Error, {
          operation: 'locationOnlyScan_updateLocation',
          stcOrderNo,
          stcLineNo: activeLine.stcLineNo,
          scannedLocation
        });
        alert(t('warehouse.modules.putaway.errors.updateLocationFailed'));
        return;
      }
    }

    // Location confirmed — go to quantity confirmation
    const remaining = activeLine.quantity - (activeLine.quantityMoved || 0);
    setScanningState({
      step: 'quantity',
      scannedItem: activeLine.itemNo || null,
      expectedLocation,
      scannedLocation,
      expectedQuantity: remaining,
      tempQuantity: remaining,
      currentLineIndex: activeLineIndex
    });

    logger.info('ModernPutAwayDetails', 'Location-only scan successful, showing quantity confirmation', {
      scannedLocation,
      expectedLocation,
      itemNo: activeLine.itemNo,
      remaining
    });
  };

  // Process global barcode scan - replicates input box logic
  const processGlobalBarcode = async (value: string) => {
    if (!value.trim()) return;

    logger.logUserAction('global_scan_input', {
      stcOrderNo,
      scannedValue: value,
      step: scanningState.step,
      activeLineIndex
    });

    const parsedData = parseBarcodeWithGS1(value);
    const { itemNo, location, quantity: gs1Quantity } = parsedData;

    switch (scanningState.step) {
      case 'item':
        await handleItemScan(itemNo);
        // If GS1 provided quantity, pre-fill it for later use
        if (gs1Quantity !== undefined) {
          console.log('📦 GS1 quantity detected:', gs1Quantity);
          // We'll use this quantity when we reach the quantity step
        }
        break;

      case 'location':
        await handleLocationScan(location);
        break;

      case 'quantity':
        // For quantity step, try to parse as number first
        // If it's a GS1 barcode with quantity, use that
        if (gs1Quantity !== undefined) {
          console.log('📦 Using GS1 quantity:', gs1Quantity);
          setScanningState({
            ...scanningState,
            tempQuantity: gs1Quantity
          });
          await handleQuantityConfirm();
        } else {
          // Try parsing as plain number
          const quantity = parseFloat(value);
          if (!isNaN(quantity) && quantity > 0) {
            setScanningState({
              ...scanningState,
              tempQuantity: quantity
            });
            await handleQuantityConfirm();
          }
        }
        break;

      default:
        break;
    }
  };

  // Handle scanner input with workflow steps
  const handleScanInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (scanningState.step === 'quantity') {
        // For quantity step, use the current value in the input field
        await handleQuantityConfirm();
        return;
      }

      // For other steps, use scanned value
      if (!scanInput.trim()) return;

      const scannedValue = scanInput.trim();
      setScanInput('');

      logger.logUserAction('scan_input', {
        stcOrderNo,
        scannedValue,
        step: scanningState.step,
        activeLineIndex,
        isLocationScanOnlyMode
      });

      // In location-scan-only mode, always route to location handler
      // (regardless of step state, since we skip item scan)
      if (isLocationScanOnlyMode && activeLine) {
        const cleanedValue = scannedValue.replace(/[~§]+$/g, '').trim();
        await handleLocationOnlyScan(cleanedValue);
        return;
      }

      const parsedData = parseBarcodeWithGS1(scannedValue);
      const { itemNo, location, quantity: gs1Quantity } = parsedData;

      switch (scanningState.step) {
        case 'item':
          await handleItemScan(itemNo);
          // If GS1 provided quantity, store it for later use
          if (gs1Quantity !== undefined) {
            console.log('📦 GS1 quantity detected from input:', gs1Quantity);
          }
          break;

        case 'location':
          await handleLocationScan(location);
          break;

        default:
          break;
      }
    }
  };

  const handleItemScan = async (scannedItem: string) => {
    const matchedLine = findLineByItem(scannedItem);

    if (!matchedLine) {
      alert(t('warehouse.modules.putaway.errors.itemNotFoundInList'));
      return;
    }

    const lineIndex = putAwayLines.indexOf(matchedLine);

    // Set as current and active line
    setCurrentLineIndex(lineIndex);
    setActiveLineIndex(lineIndex);

    // Item-only-modus: hopp rett til antallsbekreftelse siden lokasjon ikke skal skannes
    const requireLocationScan = matchedLine.requireLocationScan ?? false;
    const remaining = matchedLine.quantity - (matchedLine.quantityMoved || 0);
    const expectedLocation = matchedLine.toBinCode || '';

    setScanningState({
      ...scanningState,
      step: requireLocationScan ? 'location' : 'quantity',
      scannedItem,
      expectedLocation,
      scannedLocation: requireLocationScan ? null : expectedLocation,
      currentLineIndex: lineIndex,
      expectedQuantity: remaining,
      tempQuantity: requireLocationScan ? null : remaining
    });

    logger.info('ModernPutAwayDetails', 'Item scanned successfully', {
      scannedItem,
      lineIndex,
      expectedLocation: matchedLine.toBinCode,
      requireLocationScan,
      nextStep: requireLocationScan ? 'location' : 'quantity'
    });
  };

  const handleLocationScan = async (scannedLocation: string) => {
    const { expectedLocation, currentLineIndex } = scanningState;
    
    if (currentLineIndex === null) {
      alert(t('warehouse.modules.putaway.errors.noActiveLineFound'));
      resetScanningWorkflow();
      return;
    }
    
    const currentLine = putAwayLines[currentLineIndex];
    
    if (!currentLine) {
      alert(t('warehouse.modules.putaway.errors.lineNotFound'));
      resetScanningWorkflow();
      return;
    }
    
    // Check if location matches (allow override with confirmation)
    if (scannedLocation !== expectedLocation) {
      const confirmOverride = window.confirm(
        `${t('warehouse.modules.putaway.errors.wrongLocationScanned')}\n\n${t('warehouse.modules.putaway.labels.expected')}: ${expectedLocation}\n${t('warehouse.modules.putaway.labels.scanned')}: ${scannedLocation}\n\n${t('warehouse.modules.putaway.questions.continueAnyway')}`
      );
      
      if (!confirmOverride) {
        return;
      }
      
      // Update the toBinCode in database when location is overridden
      try {
        // Create a complete request with all existing values plus the update
        const updateRequest = {
          warehouseID: currentLine.warehouseID || 'W01',
          stcOrderNo: currentLine.stcOrderNo,
          stcLineNo: currentLine.stcLineNo,
          itemNo: currentLine.itemNo,
          quantity: currentLine.quantity,
          fromBinCode: currentLine.fromBinCode,
          toBinCode: scannedLocation, // This is the field we're updating
          grShipmentNo: currentLine.grShipmentNo,
          stcBoxNo: currentLine.stcBoxNo,
          lineStatus: currentLine.lineStatus,
          movedBy: currentLine.movedBy,
          itemGrp1: currentLine.itemGrp1,
          itemGrp2: currentLine.itemGrp2,
          itemGrp3: currentLine.itemGrp3,
          description1: currentLine.description1,
          resourceNo: currentLine.resourceNo,
          stcZoneID: currentLine.stcZoneID,
          transType: currentLine.transType,
          clientID: currentLine.clientID,
          quantityMoved: currentLine.quantityMoved,
          itemBarCode: currentLine.itemBarCode,
          externalSystemID: currentLine.externalSystemID,
          externalSystemStatus: currentLine.externalSystemStatus,
          resID: currentLine.resID,
          binGrpNo: currentLine.binGrpNo,
          sorting: currentLine.sorting,
          relID: currentLine.relID
        };
        
        await putAwayService.updatePutAwayOrderLine(stcOrderNo, currentLine.stcLineNo, updateRequest);
        
        // Update local state immediately
        setPutAwayLines(prevLines => 
          prevLines.map(line =>
            line.stcLineNo === currentLine.stcLineNo 
              ? { ...line, toBinCode: scannedLocation, location: scannedLocation }
              : line
          )
        );
        
        logger.info('ModernPutAwayDetails', 'Location updated due to override', {
          stcOrderNo,
          stcLineNo: currentLine.stcLineNo,
          oldLocation: expectedLocation,
          newLocation: scannedLocation
        });
      } catch (error) {
        logger.logException('ModernPutAwayDetails', error as Error, {
          operation: 'updateLocationOverride',
          stcOrderNo,
          stcLineNo: currentLine.stcLineNo,
          scannedLocation
        });
        
        console.error('Error updating location:', error);
        alert(t('warehouse.modules.putaway.errors.updateLocationFailed'));
        return;
      }
    }
    
    // Move to quantity confirmation
    setScanningState({
      ...scanningState,
      step: 'quantity',
      scannedLocation,
      tempQuantity: currentLine.quantity - (currentLine.quantityMoved || 0)
    });
    
    logger.info('ModernPutAwayDetails', 'Location scanned successfully', {
      scannedLocation,
      expectedLocation,
      matches: scannedLocation === expectedLocation
    });
  };

  const handleQuantityConfirm = async () => {
    const { currentLineIndex, tempQuantity } = scanningState;
    
    if (currentLineIndex === null || tempQuantity === null) {
      alert(t('warehouse.modules.putaway.errors.missingDataForConfirmation'));
      resetScanningWorkflow();
      return;
    }
    
    const currentLine = putAwayLines[currentLineIndex];
    
    if (!currentLine) {
      alert(t('warehouse.modules.putaway.errors.lineNotFound'));
      resetScanningWorkflow();
      return;
    }
    
    // Send tempQuantity direkte som delta (mengde som skal plasseres NÅ)
    // Backend håndterer split-logikk dersom dette er mindre enn gjenstående
    const quantityToPlace = tempQuantity;

    // Validering: Må ikke overstige gjenstående mengde
    const remaining = currentLine.quantity - (currentLine.quantityMoved || 0);
    if (quantityToPlace > remaining) {
      alert(`Antall (${quantityToPlace}) overstiger gjenstående (${remaining})`);
      return;
    }

    // Update quantity - quantityToPlace er delta, ikke kumulativ
    await handleUpdateQuantity(currentLine.stcLineNo, quantityToPlace, currentLineIndex);
    
    // Mark as completed for this scan
    setScanningState({
      ...scanningState,
      step: 'completed'
    });
    
    // Reset workflow after short delay for visual feedback
    setTimeout(() => {
      resetScanningWorkflow();
    }, 1000);
  };

  const handleUpdateQuantity = async (stcLineNo: number, newQuantityMoved: number, lineIndex?: number) => {
    // Prevent multiple simultaneous updates for the same line
    if (processingUpdates.has(stcLineNo)) {
      return;
    }

    // Find the line to get warehouse and bin info
    const line = putAwayLines.find(l => l.stcLineNo === stcLineNo);
    if (!line) {
      alert(t('warehouse.modules.putaway.errors.lineNotFound'));
      return;
    }

    // Validate required data BEFORE setting processing state - warehouseID must be on line
    if (!line.warehouseID || line.warehouseID.trim() === '') {
      alert(t('warehouse.modules.putaway.errors.missingWarehouseId'));
      return;
    }

    // Validate toBinCode is present
    if (!line.toBinCode || line.toBinCode.trim() === '') {
      alert(t('warehouse.modules.putaway.errors.missingDestinationLocation'));
      return;
    }

    const startTime = Date.now();

    logger.logUserAction('complete_putaway_line_with_bin', {
      stcOrderNo,
      stcLineNo,
      newQuantityMoved
    });

    // Mark this line as being processed (after validation)
    setProcessingUpdates(prev => {
      const newSet = new Set(prev);
      newSet.add(stcLineNo);
      return newSet;
    });

    try {

      // newQuantityMoved er nå DELTA (mengde som plasseres NÅ), ikke kumulativ
      const quantityToPlace = newQuantityMoved;

      // Validering
      const remaining = line.quantity - (line.quantityMoved || 0);
      if (quantityToPlace <= 0 || quantityToPlace > remaining) {
        alert(`Ugyldig antall: ${quantityToPlace}. Gjenstående: ${remaining}`);
        return;
      }

      // Call the completion endpoint that updates ItemBins and creates StcTrans
      // Backend behandler quantityToPlace som delta og håndterer split-logikk
      const response: CompletePutAwayLineResponse = await putAwayService.completePutAwayLineWithBin(
        stcOrderNo,
        stcLineNo,
        line.warehouseID,
        line.toBinCode,
        quantityToPlace
      );

      if (!response.success) {
        throw new Error(response.errorMessage || t('warehouse.modules.putaway.errors.completePutAwayFailed'));
      }

      // Log ItemBins updates for transparency
      if (response.updatedItemBins && response.updatedItemBins.length > 0) {
        logger.info('ModernPutAwayDetails', 'ItemBins oppdatert etter utplassering', {
          stcOrderNo,
          stcLineNo,
          quantityToPlace,
          isFullyPlaced: response.isFullyPlaced,
          newRestLineNo: response.newLineStcLineNo,
          updates: response.updatedItemBins.map(u => `${u.itemNo} @ ${u.binCode}: ${u.quantityChange}`)
        });
      }

      if (response.isFullyPlaced) {
        // === FULL PLASSERING — eksisterende logikk ===
        setPutAwayLines(prevLines => {
          return prevLines.map(l => {
            if (l.stcLineNo === stcLineNo) {
              return {
                ...l,
                quantityMoved: quantityToPlace,
                quantity: quantityToPlace, // Backend har evt. justert Quantity
                isPartiallyMoved: false,
                isFullyMoved: true,
                isCompleted: true
              };
            }
            return l;
          });
        });

        // Flytt plassert linje til bunn og gå til neste
        if (line && (lineIndex !== undefined || lineIndex === activeLineIndex)) {
          setTimeout(() => {
            const currentLineIndex = lineIndex !== undefined ? lineIndex :
              putAwayLines.findIndex(l => l.stcLineNo === stcLineNo);

            if (currentLineIndex !== -1) {
              moveLineToBottomAndSetNext(currentLineIndex);
            }
          }, 300);
        }
      } else {
        // === DELVIS PLASSERING (SPLIT) ===
        // Backend har redusert opprinnelig linje og opprettet ny restlinje.
        // Reload alle linjer fra backend for å sikre korrekt tilstand.
        logger.info('ModernPutAwayDetails', 'Delvis plassering — reloader linjer fra backend', {
          stcOrderNo,
          stcLineNo,
          placedQuantity: response.placedQuantity,
          remainingQuantity: response.remainingQuantity,
          newLineStcLineNo: response.newLineStcLineNo
        });

        // Reload fra backend — dette gir oss korrekt data inkl. ny restlinje
        await loadPutAwayDetails();
      }

      const duration = Date.now() - startTime;
      logger.info('ModernPutAwayDetails', 'Utplassering fullført med lageroppdatering', {
        stcOrderNo,
        stcLineNo,
        quantityMoved: quantityToPlace,
        isFullyPlaced: response.isFullyPlaced,
        duration,
        itemBinsUpdated: response.updatedItemBins.length
      });

      // After a split: re-suggest bin for the rest-line and optionally show capacity modal.
      // Two scenarios:
      //   A) System already knew correct capacity → re-suggest immediately (DB is correct)
      //   B) System had wrong capacity → show modal first, re-suggest AFTER user updates capacity
      if (!response.isFullyPlaced && line && line.toBinCode) {
        try {
          const compNo = line.companyNo;
          const bin = await stcBinService.getByExactBinCode(compNo, line.toBinCode);

          logger.info('ModernPutAwayDetails', 'Split: sjekker kapasitet', {
            itemNo: line.itemNo,
            binCode: line.toBinCode,
            binSizeNo: bin?.binSizeNo,
            quantityPlaced: quantityToPlace,
            newLineStcLineNo: response.newLineStcLineNo,
            remainingQuantity: response.remainingQuantity,
          });

          const existingConfig = bin
            ? await itemBinSizeService.getByCompanyItemAndBinSize(compNo, line.itemNo, bin.binSizeNo)
            : null;
          const systemMaxQuantity = existingConfig?.maxQuantity;

          logger.info('ModernPutAwayDetails', 'Split: kapasitetsdata lastet', {
            hasSpecificConfig: !!existingConfig,
            systemMaxQuantity,
            systemThoughtMoreWouldFit: !systemMaxQuantity || systemMaxQuantity > quantityToPlace,
          });

          // Does the system think more would fit than what was placed?
          const systemThoughtMoreWouldFit = !systemMaxQuantity || systemMaxQuantity > quantityToPlace;

          if (systemThoughtMoreWouldFit && bin) {
            // Scenario B: System had wrong capacity → show modal.
            // Re-suggestion deferred to onSaved callback (after capacity is corrected in DB).
            setCapacityModalData({
              itemNo: line.itemNo,
              itemDescription: line.description1 || line.itemNo,
              binCode: line.toBinCode,
              binSizeNo: bin.binSizeNo,
              currentQuantity: quantityToPlace,
              currentMaxQuantity: systemMaxQuantity,
              existingItemBinSizeId: existingConfig?.id ?? null,
              existingPutAwayPriorityNo: existingConfig?.putAwayPriorityNo ?? bin.putAwayPriorityNo ?? 1,
              newLineStcLineNo: response.newLineStcLineNo,
              warehouseID: line.warehouseID,
              remainingQuantity: response.remainingQuantity,
              binGrpNo: line.binGrpNo,
            });
          } else if (response.newLineStcLineNo && response.remainingQuantity && line.warehouseID) {
            // Scenario A: System already knew correct capacity → re-suggest immediately.
            // The capacity data in DB is already correct, so diagnostics will give accurate results.
            try {
              const diagResult = await putAwayDiagnosticsService.diagnose({
                itemNo: line.itemNo,
                warehouseId: line.warehouseID,
                quantity: response.remainingQuantity,
                binGrpNo: line.binGrpNo,
              });
              const newBin = diagResult.algorithmSimulation?.finalRecommendedBin;
              if (newBin && newBin !== line.toBinCode) {
                await putAwayService.updateLineDestination(stcOrderNo, response.newLineStcLineNo!, newBin);
                logger.info('ModernPutAwayDetails', 'Restlinje oppdatert med ny lokasjon etter split', {
                  stcLineNo: response.newLineStcLineNo,
                  oldBin: line.toBinCode,
                  newBin,
                });
              }
            } catch (reErr) {
              logger.info('ModernPutAwayDetails', 'Kunne ikke re-foreslå lokasjon for restlinje', { error: reErr });
            }
            // Reload to reflect new destination
            await loadPutAwayDetails();
          }
        } catch (capacityErr) {
          // Non-critical — don't block the put-away flow
          logger.info('ModernPutAwayDetails', 'Feil ved kapasitetssjekk etter split', {
            error: String(capacityErr),
            itemNo: line.itemNo,
            binCode: line.toBinCode,
          });
        }
      }

    } catch (error) {
      const duration = Date.now() - startTime;
      logger.logException('ModernPutAwayDetails', error as Error, {
        operation: 'completePutAwayLineWithBin',
        stcOrderNo,
        stcLineNo,
        duration
      });

      console.error('Feil ved fullføring av utplassering:', error);
      alert(`${t('warehouse.modules.putaway.errors.completePutAwayFailed')}: ${(error as Error).message}`);
    } finally {
      // Remove from processing set
      setProcessingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(stcLineNo);
        return newSet;
      });
    }
  };

  const handleLocationEdit = async (stcLineNo: number, field: 'from' | 'to', newLocation: string) => {
    logger.logUserAction('edit_putaway_location', {
      stcOrderNo,
      stcLineNo,
      field,
      newLocation
    });

    try {
      // Find the current line
      const currentLine = putAwayLines.find(l => l.stcLineNo === stcLineNo);
      if (!currentLine) {
        alert(t('warehouse.modules.putaway.errors.lineNotFound'));
        return;
      }
      
      // Create a complete request with all existing values plus the update
      const updateRequest = {
        warehouseID: currentLine.warehouseID || 'W01',
        stcOrderNo: currentLine.stcOrderNo,
        stcLineNo: currentLine.stcLineNo,
        itemNo: currentLine.itemNo,
        quantity: currentLine.quantity,
        fromBinCode: field === 'from' ? newLocation : currentLine.fromBinCode,
        toBinCode: field === 'to' ? newLocation : currentLine.toBinCode,
        grShipmentNo: currentLine.grShipmentNo,
        stcBoxNo: currentLine.stcBoxNo,
        lineStatus: currentLine.lineStatus,
        movedBy: currentLine.movedBy,
        itemGrp1: currentLine.itemGrp1,
        itemGrp2: currentLine.itemGrp2,
        itemGrp3: currentLine.itemGrp3,
        description1: currentLine.description1,
        resourceNo: currentLine.resourceNo,
        stcZoneID: currentLine.stcZoneID,
        transType: currentLine.transType,
        clientID: currentLine.clientID,
        quantityMoved: currentLine.quantityMoved,
        itemBarCode: currentLine.itemBarCode,
        externalSystemID: currentLine.externalSystemID,
        externalSystemStatus: currentLine.externalSystemStatus,
        resID: currentLine.resID,
        binGrpNo: currentLine.binGrpNo,
        sorting: currentLine.sorting,
        relID: currentLine.relID
      };

      await putAwayService.updatePutAwayOrderLine(stcOrderNo, stcLineNo, updateRequest);
      
      setPutAwayLines(prevLines => 
        prevLines.map(line =>
          line.stcLineNo === stcLineNo 
            ? { 
                ...line, 
                fromBinCode: field === 'from' ? newLocation : line.fromBinCode,
                toBinCode: field === 'to' ? newLocation : line.toBinCode,
                location: field === 'to' ? newLocation : line.location 
              }
            : line
        )
      );
      
      if (field === 'from') {
        setEditingFromLocation(null);
        setNewFromLocation('');
      } else {
        setEditingToLocation(null);
        setNewToLocation('');
      }
      
      logger.info('ModernPutAwayDetails', 'Successfully updated location', {
        stcOrderNo,
        stcLineNo,
        field,
        newLocation
      });
    } catch (error) {
      logger.logException('ModernPutAwayDetails', error as Error, {
        operation: 'updateLocation',
        stcOrderNo,
        stcLineNo,
        field,
        newLocation
      });
      
      console.error('Error updating location:', error);
      alert(t('warehouse.modules.putaway.errors.updateLocationFailed'));
    }
  };

  const handleQuantityEdit = async (stcLineNo: number, newQty: number) => {
    // newQty er mengden som skal plasseres (delta), behandlet som delta i handleUpdateQuantity
    await handleUpdateQuantity(stcLineNo, newQty);
    setEditingQuantity(null);
    setNewQuantity('');
  };

  const handleCompletePutAway = async () => {
    logger.logUserAction('complete_putaway', { stcOrderNo });

    if (window.confirm(t('warehouse.modules.putaway.questions.confirmComplete'))) {
      try {
        await putAwayService.completePutAway(stcOrderNo);

        logger.info('ModernPutAwayDetails', 'Successfully completed put-away', { stcOrderNo });

        onBack();
      } catch (error) {
        logger.logException('ModernPutAwayDetails', error as Error, {
          operation: 'completePutAway',
          stcOrderNo
        });

        console.error('Error completing put-away:', error);
        alert(t('warehouse.modules.putaway.errors.completeOrderFailed'));
      }
    }
  };

  // Bytt foreslått toBinCode for aktiv linje (uten å plassere)
  // Brukes når operatør ikke skanner lokasjon og vil avvike fra systemets forslag
  const handleChangeActiveLineLocation = async (newBinCode: string) => {
    if (activeLineIndex === null || !activeLine) {
      alert(t('warehouse.modules.putaway.errors.noActiveLineFound'));
      return;
    }

    if (!newBinCode || newBinCode.trim() === '') {
      return;
    }

    if (newBinCode === activeLine.toBinCode) {
      // Ingen endring — bare lukk modalen
      setShowChangeLocationSearch(false);
      return;
    }

    logger.logUserAction('change_active_line_location', {
      stcOrderNo,
      stcLineNo: activeLine.stcLineNo,
      oldBinCode: activeLine.toBinCode,
      newBinCode,
    });

    setIsChangingLocation(true);

    try {
      // Bruk dedikert PATCH-endepunkt for kun toBinCode (samme som auto-resuggestion etter split)
      await putAwayService.updateLineDestination(stcOrderNo, activeLine.stcLineNo, newBinCode);

      // Oppdater lokal state
      setPutAwayLines(prevLines =>
        prevLines.map(line =>
          line.stcLineNo === activeLine.stcLineNo
            ? { ...line, toBinCode: newBinCode, location: newBinCode }
            : line
        )
      );

      // Hvis vi var midt i lokasjonsskanning-steget, marker som bekreftet og gå til antall
      if (scanningState.step === 'location' && scanningState.currentLineIndex === activeLineIndex) {
        const remaining = activeLine.quantity - (activeLine.quantityMoved || 0);
        setScanningState({
          ...scanningState,
          step: 'quantity',
          expectedLocation: newBinCode,
          scannedLocation: newBinCode,
          tempQuantity: remaining,
        });
      }

      logger.info('ModernPutAwayDetails', 'Aktiv linje fikk ny destinasjon via Bytt lokasjon', {
        stcOrderNo,
        stcLineNo: activeLine.stcLineNo,
        oldBinCode: activeLine.toBinCode,
        newBinCode,
      });

      setShowChangeLocationSearch(false);
    } catch (error) {
      logger.logException('ModernPutAwayDetails', error as Error, {
        operation: 'changeActiveLineLocation',
        stcOrderNo,
        stcLineNo: activeLine.stcLineNo,
        newBinCode,
      });
      alert(t('warehouse.modules.putaway.errors.updateLocationFailed'));
    } finally {
      setIsChangingLocation(false);
    }
  };

  const handleBulkCompleteToBin = async () => {
    if (!bulkToBinCode || bulkToBinCode.trim() === '') {
      alert(t('warehouse.modules.putaway.errors.pleaseProvideDestination'));
      return;
    }

    const incompleteLines = putAwayLines.filter(l => (l.quantityMoved || 0) === 0);

    if (incompleteLines.length === 0) {
      alert(t('warehouse.modules.putaway.messages.allLinesCompleted'));
      setShowBulkModal(false);
      return;
    }

    // Validate all incomplete lines have the same warehouseID
    const warehouseIDs = new Set(
      incompleteLines
        .map(l => l.warehouseID)
        .filter(id => id && id.trim() !== '')
    );

    if (warehouseIDs.size === 0) {
      alert(t('warehouse.modules.putaway.errors.missingWarehouseIdBulk'));
      return;
    }

    if (warehouseIDs.size > 1) {
      alert(t('warehouse.modules.putaway.errors.differentWarehouses') + '\n\n' +
            t('warehouse.modules.putaway.labels.foundWarehouses') + ': ' + Array.from(warehouseIDs).join(', '));
      return;
    }

    const warehouseID = Array.from(warehouseIDs)[0];

    logger.logUserAction('bulk_complete_putaway_to_bin', {
      stcOrderNo,
      toBinCode: bulkToBinCode,
      incompleteCount: incompleteLines.length,
      warehouseID
    });

    setIsBulkProcessing(true);

    try {
      const response = await putAwayService.completePutAwayBulkToBin(
        stcOrderNo,
        warehouseID,
        bulkToBinCode
      );

      if (!response.success) {
        throw new Error(response.errorMessage || 'Kunne ikke fullføre bulk-plassering');
      }

      logger.info('ModernPutAwayDetails', 'Bulk-plassering fullført', {
        stcOrderNo,
        completedCount: response.completedCount,
        completedLines: response.completedLines.map(l => l.stcLineNo)
      });

      // Reload the entire put-away details to get fresh data
      await loadPutAwayDetails();

      // Keep modal open to show result (based on user feedback)
      alert(t('warehouse.modules.putaway.messages.bulkCompleted', { '0': response.completedCount.toString(), '1': bulkToBinCode }));

      // Close modal after user acknowledges
      setShowBulkModal(false);
      setBulkToBinCode('');

    } catch (error) {
      logger.logException('ModernPutAwayDetails', error as Error, {
        operation: 'bulkCompleteToBin',
        stcOrderNo,
        toBinCode: bulkToBinCode
      });

      console.error('Feil ved bulk-plassering:', error);
      alert(`${t('warehouse.modules.putaway.errors.bulkCompleteFailed')}: ${(error as Error).message}`);
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 100: return 'bg-green-100 text-green-800';
      case 110: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScanningStepInfo = () => {
    // In location-scan-only mode, adapt messaging
    if (isLocationScanOnlyMode) {
      switch (scanningState.step) {
        case 'location':
          return {
            title: t('warehouse.modules.putaway.scanning.scanLocation'),
            description: activeLine
              ? `${activeLine.itemNo} → ${activeLine.toBinCode || '?'}`
              : t('warehouse.modules.putaway.scanning.scanDestinationLocation'),
            icon: MapPin,
            color: 'text-orange-600'
          };
        case 'quantity':
          return {
            title: t('warehouse.modules.putaway.scanning.confirmQuantity'),
            description: `${t('warehouse.modules.putaway.labels.quantity')}: ${scanningState.tempQuantity || 0}`,
            icon: AlertCircle,
            color: 'text-green-600'
          };
        case 'completed':
          return {
            title: t('warehouse.modules.putaway.scanning.completed'),
            description: t('warehouse.modules.putaway.scanning.itemPlaced'),
            icon: CheckCircle2,
            color: 'text-green-600'
          };
        default:
          return {
            title: t('warehouse.modules.putaway.scanning.scanLocation'),
            description: activeLine
              ? `${activeLine.itemNo} → ${activeLine.toBinCode || '?'}`
              : t('warehouse.modules.putaway.scanning.startByScanning'),
            icon: MapPin,
            color: 'text-orange-600'
          };
      }
    }

    // No scan mode — manual operation
    if (isNoScanMode) {
      return {
        title: t('warehouse.modules.putaway.scanning.manualMode', {}, 'Manuell plassering'),
        description: t('warehouse.modules.putaway.scanning.useButtonsToPlace', {}, 'Bruk Plasser-knappene for å plassere varer'),
        icon: Package,
        color: 'text-gray-600'
      };
    }

    // Default: item → location → quantity (both scan or item-only)
    switch (scanningState.step) {
      case 'item':
        return {
          title: t('warehouse.modules.putaway.scanning.scanItem'),
          description: activeLine ? `${t('warehouse.modules.putaway.labels.expected')}: ${activeLine.itemNo}` : t('warehouse.modules.putaway.scanning.scanItemBarcode'),
          icon: Package,
          color: 'text-blue-600'
        };
      case 'location':
        return {
          title: t('warehouse.modules.putaway.scanning.scanLocation'),
          description: scanningState.expectedLocation ? `${t('warehouse.modules.putaway.labels.expected')}: ${scanningState.expectedLocation}` : t('warehouse.modules.putaway.scanning.scanDestinationLocation'),
          icon: MapPin,
          color: 'text-orange-600'
        };
      case 'quantity':
        return {
          title: t('warehouse.modules.putaway.scanning.confirmQuantity'),
          description: `${t('warehouse.modules.putaway.labels.quantity')}: ${scanningState.tempQuantity || 0}`,
          icon: AlertCircle,
          color: 'text-green-600'
        };
      case 'completed':
        return {
          title: t('warehouse.modules.putaway.scanning.completed'),
          description: t('warehouse.modules.putaway.scanning.itemPlaced'),
          icon: CheckCircle2,
          color: 'text-green-600'
        };
      default:
        return {
          title: t('warehouse.modules.putaway.scanning.scanItem'),
          description: t('warehouse.modules.putaway.scanning.startByScanning'),
          icon: Package,
          color: 'text-blue-600'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12 px-4">
        <div className="w-6 h-6 sm:w-8 sm:h-8 animate-spin border-2 border-green-500 border-t-transparent rounded-full" />
        <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-600">{t('warehouse.modules.putaway.messages.loadingDetails')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <p className="text-red-600 mb-4 text-sm sm:text-base">{error}</p>
        <button
          onClick={() => {
            logger.logUserAction('retry_load_putaway_details');
            loadPutAwayDetails();
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 min-h-[44px] text-sm sm:text-base"
        >
          {t('common.buttons.tryAgain')}
        </button>
      </div>
    );
  }

  if (!putAwayDetails) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <p className="text-gray-600 text-sm sm:text-base">{t('warehouse.modules.putaway.errors.orderNotFound')}</p>
      </div>
    );
  }

  const scanStepInfo = getScanningStepInfo();
  const StepIcon = scanStepInfo.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);
          // In location-scan-only mode, route directly to location handler
          if (isLocationScanOnlyMode && activeLine) {
            const cleanedBarcode = barcode.replace(/[~§]+$/g, '').trim();
            handleLocationOnlyScan(cleanedBarcode);
            return;
          }
          processGlobalBarcode(barcode);
        }}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-2 sm:p-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <BackButton
              onClick={onBack}
              onBeforeNavigate={() => {
                logger.logUserAction('back_from_putaway_details', { stcOrderNo });
              }}
            />
            <h1 className="text-base sm:text-lg lg:text-xl font-semibold truncate">
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.title')} #{putAwayDetails.header.stcOrderNo}</span>
              <span className="sm:hidden">#{putAwayDetails.header.stcOrderNo}</span>
            </h1>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button className="flex items-center justify-center min-w-[44px] min-h-[44px] w-9 h-9 sm:w-10 sm:h-10 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Printer size={16} className="sm:hidden" />
              <Printer size={20} className="hidden sm:block" />
            </button>
            <button
              type="button"
              onClick={() => setShowExtendShelfModal(true)}
              title={t('warehouse.extendShelf.buttonTitle')}
              aria-label={t('warehouse.extendShelf.buttonTitle')}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] w-9 h-9 sm:w-10 sm:h-10 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
            >
              <PlusSquare size={16} className="sm:hidden" />
              <PlusSquare size={20} className="hidden sm:block" />
            </button>
            <MediaCaptureButton
              entityType="PutAway"
              entityKey={stcOrderNo.toString()}
            />
            <button className="flex items-center justify-center min-w-[44px] min-h-[44px] w-9 h-9 sm:w-10 sm:h-10 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <BarChart3 size={16} className="sm:hidden" />
              <BarChart3 size={20} className="hidden sm:block" />
            </button>
            <button className="flex items-center justify-center min-w-[44px] min-h-[44px] w-9 h-9 sm:w-10 sm:h-10 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Info size={16} className="sm:hidden" />
              <Info size={20} className="hidden sm:block" />
            </button>
            <button className="flex items-center justify-center min-w-[44px] min-h-[44px] w-9 h-9 sm:w-10 sm:h-10 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Settings size={16} className="sm:hidden" />
              <Settings size={20} className="hidden sm:block" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Scanner Section with Workflow Steps */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 mb-3 sm:mb-6">
          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <StepIcon className={`${scanStepInfo.color} w-5 h-5 sm:w-6 sm:h-6`} />
            <h2 className="text-base sm:text-lg font-semibold">{scanStepInfo.title}</h2>
          </div>
          
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{scanStepInfo.description}</p>
          
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <ScanTextInput
              ref={scannerRef}
              value={scanningState.step === 'quantity' ? (scanningState.tempQuantity || 0).toString() : scanInput}
              onChange={(e) => {
                if (scanningState.step === 'quantity') {
                  const newQuantity = parseFloat(e.target.value) || 0;
                  setScanningState({
                    ...scanningState,
                    tempQuantity: newQuantity
                  });
                } else {
                  setScanInput(e.target.value);
                }
              }}
              onKeyDown={handleScanInput}
              placeholder={
                scanningState.step === 'quantity'
                  ? t('warehouse.modules.putaway.scanning.changeQuantityAndPress')
                  : isLocationScanOnlyMode
                    ? t('warehouse.modules.putaway.scanning.scanLocationPlaceholder', {}, 'Skann lokasjon...')
                    : t('warehouse.modules.putaway.scanning.scan')
              }
              className="flex-1 p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
              disabled={scanningState.step === 'completed'}
            />
            {scanningState.step === 'quantity' && (
              <button
                onClick={handleQuantityConfirm}
                className="bg-green-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base min-h-[44px] min-w-[80px]"
              >
                {t('common.buttons.confirm')}
              </button>
            )}
          </div>

          {/* Workflow Progress Indicator - adapts to scanning mode */}
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            {/* Step 1: Item scan — hidden in location-only and no-scan mode */}
            {!isLocationScanOnlyMode && !isNoScanMode && (
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${
                ['item', 'location', 'quantity', 'completed'].indexOf(scanningState.step) >= 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <Package size={12} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">1. {t('warehouse.modules.putaway.steps.item')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.steps.itemShort')}</span>
                {scanningState.scannedItem && <CheckCircle2 size={12} className="sm:w-4 sm:h-4" />}
              </div>
            )}
            {/* Step 2: Location scan — shown for location-only, both-scan, and item+location */}
            {(isLocationScanOnlyMode || isBothScanMode) && (
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${
                ['location', 'quantity', 'completed'].indexOf(scanningState.step) >= 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <MapPin size={12} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{isLocationScanOnlyMode ? '1' : '2'}. {t('warehouse.modules.putaway.steps.location')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.steps.locationShort')}</span>
                {scanningState.scannedLocation && <CheckCircle2 size={12} className="sm:w-4 sm:h-4" />}
              </div>
            )}
            {/* Step 3: Quantity — always shown when scanning is active */}
            {!isNoScanMode && (
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${
                ['quantity', 'completed'].indexOf(scanningState.step) >= 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <AlertCircle size={12} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{isLocationScanOnlyMode ? '2' : isItemScanOnlyMode ? '2' : '3'}. {t('warehouse.modules.putaway.steps.quantity')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.steps.quantityShort')}</span>
                {scanningState.step === 'completed' && <CheckCircle2 size={12} className="sm:w-4 sm:h-4" />}
              </div>
            )}
          </div>
        </div>

        {/* Active Line - Much more compact layout */}
        {activeLine && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-2 sm:p-6 mb-3 sm:mb-6 shadow-sm">
            {/* Header-rad: tittel til venstre, Plasser-knapp til høyre.
                Knappen lever i header for å spare vertikal plass på enheter med begrenset skjermhøyde. */}
            <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4">
              <h2 className="text-sm sm:text-lg font-semibold text-green-800 flex items-center gap-2 min-w-0">
                <Package size={14} className="sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="hidden sm:inline">{t('warehouse.modules.putaway.labels.activeLine')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.labels.active')}</span>
              </h2>
              {(activeLine.quantityMoved || 0) < activeLine.quantity && (
                <button
                  onClick={() => {
                    if (activeLineIndex !== null) {
                      handleUpdateQuantity(activeLine.stcLineNo, activeLine.quantity, activeLineIndex);
                    }
                  }}
                  disabled={processingUpdates.has(activeLine.stcLineNo)}
                  className="flex items-center gap-1.5 bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm font-semibold min-h-[36px] sm:min-h-[40px] flex-shrink-0"
                >
                  <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {processingUpdates.has(activeLine.stcLineNo)
                    ? t('warehouse.modules.putaway.actions.working')
                    : `${t('warehouse.modules.putaway.actions.place')} ${activeLine.quantity - (activeLine.quantityMoved || 0)}`}
                </button>
              )}
            </div>
            {/* Mobile: Horizontal compact layout, Desktop: Grid */}
            <div className="sm:hidden">
              {/* Mobile: Single row with all key info */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="text-sm font-semibold text-green-900">{activeLine.itemNo}</div>
                  <div className="text-xs text-green-600">→</div>
                  <div className="text-xs text-green-900">{activeLine.toBinCode || 'N/A'}</div>
                </div>
                <div className="text-xs text-green-900 font-medium">
                  {activeLine.quantityMoved || 0}/{activeLine.quantity}
                </div>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-green-200 rounded-full h-1.5">
                <div
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${activeLine.quantity > 0 ? ((activeLine.quantityMoved || 0) / activeLine.quantity) * 100 : 0}%` }}
                />
              </div>
              {/* Description on second line if needed */}
              {activeLine.description1 && (
                <div className="text-xs text-green-600 mt-1 truncate">{activeLine.description1}</div>
              )}
            </div>
            {/* Desktop: Original grid layout */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
              <div>
                <label className="text-xs sm:text-sm font-medium text-green-700">{t('warehouse.general.item')}</label>
                <div className="text-sm sm:text-lg font-semibold text-green-900">{activeLine.itemNo}</div>
                <div className="text-xs sm:text-sm text-green-600 truncate">{activeLine.description1}</div>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-medium text-green-700">{t('warehouse.modules.putaway.labels.from')}</label>
                <div className="text-sm sm:text-lg text-green-900">{activeLine.fromBinCode || t('common.labels.notAssigned')}</div>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-medium text-green-700">{t('warehouse.modules.putaway.labels.to')}</label>
                <div className="text-sm sm:text-lg text-green-900">{activeLine.toBinCode || t('common.labels.notAssigned')}</div>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-medium text-green-700">{t('warehouse.modules.putaway.labels.progress')}</label>
                <div className="text-sm sm:text-lg text-green-900">{activeLine.quantityMoved || 0} / {activeLine.quantity}</div>
                <div className="w-full bg-green-200 rounded-full h-1.5 sm:h-2 mt-1">
                  <div
                    className="bg-green-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                    style={{ width: `${activeLine.quantity > 0 ? ((activeLine.quantityMoved || 0) / activeLine.quantity) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Lines List - More compact on mobile */}
        <div className="bg-white rounded-lg shadow-sm mb-20">
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-base sm:text-lg font-semibold">{t('warehouse.modules.putaway.labels.lines')} ({putAwayLines.length})</h2>
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm text-gray-600">
                  {t('warehouse.modules.putaway.labels.completed')}: {completedLines} / {putAwayLines.length}
                </span>
                <div className="w-16 sm:w-32 bg-gray-200 rounded-full h-1.5 sm:h-2">
                  <div
                    className="bg-green-500 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                    style={{ width: `${putAwayLines.length > 0 ? (completedLines / putAwayLines.length) * 100 : 0}%` }}
                  />
                </div>
                {deferredLineNos.size > 0 && (
                  <button
                    onClick={resetLineOrder}
                    className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-orange-200 transition-colors text-xs sm:text-sm min-h-[44px]"
                    title="Tilbakestill rekkefølge"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Tilbakestill</span>
                    <span className="sm:hidden">{deferredLineNos.size}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowBulkModal(true)}
                  disabled={putAwayLines.filter(l => (l.quantityMoved || 0) === 0).length === 0}
                  className="bg-purple-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-purple-700 transition-colors text-xs sm:text-sm disabled:bg-purple-400 disabled:cursor-not-allowed min-h-[44px]"
                  title="Plasser alle gjenstående linjer til samme lokasjon"
                >
                  <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.placeAll')}</span>
                  <span className="sm:hidden">{t('warehouse.modules.putaway.actions.bulk')}</span>
                </button>
                <button
                  onClick={() => setShowChangeLocationSearch(true)}
                  disabled={!activeLine || isChangingLocation}
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm disabled:bg-blue-300 disabled:cursor-not-allowed min-h-[44px]"
                  title={t('warehouse.modules.putaway.actions.changeLocationTooltip', {}, 'Overstyr foreslått lokasjon for aktiv linje')}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.changeLocation', {}, 'Bytt lokasjon')}</span>
                  <span className="sm:hidden">{t('warehouse.modules.putaway.actions.changeLocationShort', {}, 'Bytt')}</span>
                </button>
                <button
                  onClick={handleCompletePutAway}
                  disabled={!allLinesFullyPlaced}
                  className="bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px]"
                  title={!allLinesFullyPlaced ? 'Alle linjer må være plassert før utplassering kan fullføres' : ''}
                >
                  <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.completePutaway')}</span>
                  <span className="sm:hidden">{t('warehouse.modules.putaway.actions.complete')}</span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {putAwayLines.map((line, index) => (
              <SwipeablePutAwayLine
                key={line.stcLineNo}
                stcLineNo={line.stcLineNo}
                enabled={(line.quantityMoved || 0) === 0 && !deferredLineNos.has(line.stcLineNo)}
                isDeferred={deferredLineNos.has(line.stcLineNo)}
                onSwipeToBottom={moveLineToBottom}
              >
              <div
                className={`p-3 sm:p-4 cursor-pointer transition-all duration-200 ${
                  index === currentLineIndex
                    ? 'bg-green-50 border-l-4 border-green-500 shadow-inner'
                    : index === activeLineIndex
                    ? 'bg-blue-50 border-l-4 border-blue-500 shadow-inner'
                    : deferredLineNos.has(line.stcLineNo)
                    ? 'border-l-4 border-orange-400 bg-orange-50/30 hover:bg-orange-50/50'
                    : 'hover:bg-gray-50'
                } ${(line.quantityMoved || 0) > 0 ? 'opacity-75' : ''}`}
                onClick={() => handleLineSelect(index)}
              >
                {/* Mobile layout: Stack items vertically */}
                <div className="sm:hidden space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        #{line.stcLineNo}: {line.itemNo}
                      </div>
                      {index === activeLineIndex && (
                        <div className="text-xs font-medium text-blue-600">{t('warehouse.modules.putaway.labels.active').toUpperCase()}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {line.quantityMoved || 0} / {line.quantity}
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600 truncate">
                    {line.description1}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-600">
                      {line.fromBinCode || t('common.labels.notAssigned')} → {line.toBinCode || t('common.labels.notAssigned')}
                    </div>
                    <div className="flex items-center gap-1">
                      {(line.quantityMoved || 0) === 0 && (
                        /* Plasser-knapp er alltid tilgjengelig som fallback (også når skanning kreves) */
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateQuantity(line.stcLineNo, line.quantity, index);
                          }}
                          disabled={processingUpdates.has(line.stcLineNo)}
                          className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:bg-green-400 transition-colors min-h-[36px] min-w-[60px]"
                        >
                          {processingUpdates.has(line.stcLineNo) ? '...' : t('warehouse.modules.putaway.actions.place')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop layout: Grid */}
                <div className="hidden sm:grid sm:grid-cols-6 gap-4 items-center">
                  <div>
                    <div className="font-medium text-gray-900">
                      #{line.stcLineNo}: {line.itemNo}
                    </div>
                    <div className="text-sm text-gray-600 truncate">{line.description1}</div>
                    {index === activeLineIndex && (
                      <div className="text-xs font-medium text-blue-600 mt-1">{t('warehouse.modules.putaway.labels.activeLine').toUpperCase()}</div>
                    )}
                  </div>
                  
                  <div>
                    {editingFromLocation === line.stcLineNo ? (
                      <ScanTextInput
                        value={newFromLocation}
                        onChange={(e) => setNewFromLocation(e.target.value)}
                        onBlur={() => handleLocationEdit(line.stcLineNo, 'from', newFromLocation)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLocationEdit(line.stcLineNo, 'from', newFromLocation)}
                        className="w-full p-1 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                        autoFocus
                      />
                    ) : (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingFromLocation(line.stcLineNo);
                          setNewFromLocation(line.fromBinCode || '');
                        }}
                        className="text-sm text-gray-900 hover:text-green-600 cursor-pointer"
                      >
                        {t('warehouse.modules.putaway.labels.from')}: {line.fromBinCode || t('common.labels.notAssigned')}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    {editingToLocation === line.stcLineNo ? (
                      <ScanTextInput
                        value={newToLocation}
                        onChange={(e) => setNewToLocation(e.target.value)}
                        onBlur={() => handleLocationEdit(line.stcLineNo, 'to', newToLocation)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLocationEdit(line.stcLineNo, 'to', newToLocation)}
                        className="w-full p-1 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                        autoFocus
                      />
                    ) : (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingToLocation(line.stcLineNo);
                          setNewToLocation(line.toBinCode || '');
                        }}
                        className="text-sm text-gray-900 hover:text-green-600 cursor-pointer"
                      >
                        {t('warehouse.modules.putaway.labels.to')}: {line.toBinCode || t('common.labels.notAssigned')}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    {editingQuantity === line.stcLineNo ? (
                      <NumericInput
                        mode="decimal"
                        value={newQuantity}
                        onChange={(e) => setNewQuantity(e.target.value)}
                        onBlur={() => handleQuantityEdit(line.stcLineNo, parseFloat(newQuantity) || 0)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuantityEdit(line.stcLineNo, parseFloat(newQuantity) || 0)}
                        className="w-full p-1 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                        autoFocus
                      />
                    ) : (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingQuantity(line.stcLineNo);
                          setNewQuantity((line.quantityMoved || 0).toString());
                        }}
                        className="text-sm text-gray-900 hover:text-green-600 cursor-pointer"
                      >
                        {line.quantityMoved || 0} / {line.quantity}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                      {line.statusText}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {(line.quantityMoved || 0) === 0 && (
                      /* Plasser-knapp er alltid tilgjengelig som fallback (også når skanning kreves) */
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateQuantity(line.stcLineNo, line.quantity, index);
                        }}
                        disabled={processingUpdates.has(line.stcLineNo)}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:bg-green-400 transition-colors min-h-[44px]"
                      >
                        {processingUpdates.has(line.stcLineNo) ? t('warehouse.modules.putaway.actions.working') : t('warehouse.modules.putaway.actions.place')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              </SwipeablePutAwayLine>
            ))}
          </div>
        </div>
      </div>

      {/* Status Bar - More compact on mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 sm:p-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-xs sm:text-sm font-medium text-gray-900">
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.labels.progress')}: </span>{completedLines} / {putAwayLines.length}
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="w-16 sm:w-32 bg-gray-200 rounded-full h-1.5 sm:h-2">
                <div
                  className="bg-green-500 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                  style={{ width: `${putAwayLines.length > 0 ? (completedLines / putAwayLines.length) * 100 : 0}%` }}
                />
              </div>
              <span className="text-xs sm:text-sm text-gray-600">
                {putAwayLines.length > 0 ? Math.round((completedLines / putAwayLines.length) * 100) : 0}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-600">
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.labels.priority')}: </span>P: {putAwayDetails.header.orderPriority}
            </div>
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              scanningState.step === 'completed' 
                ? 'bg-green-100 text-green-800'
                : scanningState.step === 'item'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {scanStepInfo.title}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Completion Modal - Fullscreen on mobile */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl max-w-md sm:w-full p-4 sm:p-6 flex flex-col">
            <h2 className="text-lg sm:text-xl font-semibold mb-4">{t('warehouse.modules.putaway.bulk.title')}</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {t('warehouse.modules.putaway.bulk.description', { '0': putAwayLines.filter(l => (l.quantityMoved || 0) === 0).length.toString() })}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                {t('warehouse.modules.putaway.bulk.systemNote')}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('warehouse.modules.putaway.bulk.destinationLocation')} *
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => !isBulkProcessing && setShowBulkLocationSearch(true)}
                  disabled={isBulkProcessing}
                  className={`flex-1 p-3 border rounded-lg text-left transition-colors flex items-center justify-between min-h-[48px] ${
                    bulkToBinCode
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-gray-300 hover:border-purple-400 cursor-pointer'
                  } ${isBulkProcessing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  {bulkToBinCode ? (
                    <span className="font-semibold text-purple-800 uppercase">{bulkToBinCode}</span>
                  ) : (
                    <span className="text-gray-400">{t('warehouse.modules.putaway.bulk.scanOrEnterLocation')}</span>
                  )}
                  <Search className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </button>
                {bulkToBinCode && !isBulkProcessing && (
                  <button
                    type="button"
                    onClick={() => setBulkToBinCode('')}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('common.buttons.clear', {}, 'Fjern')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-auto">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkToBinCode('');
                }}
                disabled={isBulkProcessing}
                className="w-full sm:flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px]"
              >
                {t('common.buttons.cancel')}
              </button>
              <button
                onClick={handleBulkCompleteToBin}
                disabled={isBulkProcessing || !bulkToBinCode.trim()}
                className="w-full sm:flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed min-h-[44px]"
              >
                {isBulkProcessing ? t('warehouse.modules.putaway.bulk.placing') : t('common.buttons.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capacity Update Modal — shown after split/partial placement */}
      {capacityModalData && (
        <CapacityUpdateModal
          isOpen={true}
          onClose={() => setCapacityModalData(null)}
          onSaved={async (newMax) => {
            // Read from ref to avoid stale closure (React may have re-rendered between modal open and save)
            const data = capacityModalDataRef.current;
            if (!data) return;

            logger.info('ModernPutAwayDetails', 'Kapasitet oppdatert fra utlegg — starter re-forslag', {
              itemNo: data.itemNo,
              binCode: data.binCode,
              newMaxQuantity: newMax,
              newLineStcLineNo: data.newLineStcLineNo,
              warehouseID: data.warehouseID,
              remainingQuantity: data.remainingQuantity,
              binGrpNo: data.binGrpNo,
            });

            // Re-suggest bin for the rest-line now that capacity is updated
            if (data.newLineStcLineNo && data.warehouseID && data.remainingQuantity) {
              try {
                const diagResult = await putAwayDiagnosticsService.diagnose({
                  itemNo: data.itemNo,
                  warehouseId: data.warehouseID,
                  quantity: data.remainingQuantity,
                  binGrpNo: data.binGrpNo,
                });

                const newBin = diagResult.algorithmSimulation?.finalRecommendedBin;
                if (newBin && newBin !== data.binCode) {
                  await putAwayService.updateLineDestination(stcOrderNo, data.newLineStcLineNo, newBin);
                  logger.info('ModernPutAwayDetails', 'Restlinje oppdatert med ny lokasjon', {
                    stcLineNo: data.newLineStcLineNo,
                    oldBin: data.binCode,
                    newBin,
                  });
                } else {
                  logger.info('ModernPutAwayDetails', 'Diagnostikk foreslo samme eller ingen ny lokasjon', {
                    suggestedBin: newBin,
                    currentBin: data.binCode,
                  });
                }
              } catch (reErr) {
                logger.info('ModernPutAwayDetails', 'Kunne ikke re-foreslå lokasjon for restlinje', { error: String(reErr) });
              }

              // Reload lines to reflect updated destination
              await loadPutAwayDetails();
            } else {
              logger.info('ModernPutAwayDetails', 'Mangler data for re-forslag, hopper over', {
                newLineStcLineNo: data.newLineStcLineNo,
                warehouseID: data.warehouseID,
                remainingQuantity: data.remainingQuantity,
              });
            }

            setCapacityModalData(null);
          }}
          itemNo={capacityModalData.itemNo}
          itemDescription={capacityModalData.itemDescription}
          binCode={capacityModalData.binCode}
          binSizeNo={capacityModalData.binSizeNo}
          currentQuantity={capacityModalData.currentQuantity}
          currentMaxQuantity={capacityModalData.currentMaxQuantity}
          existingItemBinSizeId={capacityModalData.existingItemBinSizeId}
          existingPutAwayPriorityNo={capacityModalData.existingPutAwayPriorityNo}
        />
      )}

      {/* Location Search Modal for bulk placement */}
      <LocationSearchModal
        isOpen={showBulkLocationSearch}
        onClose={() => setShowBulkLocationSearch(false)}
        onSelect={(location: LocationSearchResult) => {
          setBulkToBinCode(location.binCode);
          setShowBulkLocationSearch(false);
        }}
        title={t('warehouse.modules.putaway.bulk.selectDestination', {}, 'Velg destinasjonslokasjon')}
      />

      {/* Bytt lokasjon-modal: overstyr foreslått toBinCode for aktiv linje.
          Min 2 tegn før søk for å unngå unødvendig last på enheter med få lokasjoner skannet. */}
      <LocationSearchModal
        isOpen={showChangeLocationSearch}
        onClose={() => !isChangingLocation && setShowChangeLocationSearch(false)}
        onSelect={(location: LocationSearchResult) => {
          handleChangeActiveLineLocation(location.binCode);
        }}
        title={t('warehouse.modules.putaway.actions.changeLocation', {}, 'Bytt lokasjon')}
        minSearchLength={2}
      />

      {/* Extend Shelf Modal — lar operatør legge til ny lokasjon på eksisterende hylle.
          Blank prefill: operatør scanner/skriver parent-BinCode for hyllen de vil utvide.
          Ved opprettelse: sett ny bin som destinasjon for aktiv linje, persister til DB,
          og avanser scan-state (hopper over location-skanning siden destinasjon er bekreftet). */}
      <ExtendShelfModal
        isOpen={showExtendShelfModal}
        onClose={() => setShowExtendShelfModal(false)}
        initialParentBinCode=""
        onCreated={async (newBinCode) => {
          logger.logUserAction('extend_shelf_created', {
            newBinCode,
            activeLineIndex,
            stcOrderNo,
          });

          // Hvis det er en aktiv linje, sett ny lokasjon som destinasjon
          if (activeLine && activeLineIndex !== null) {
            try {
              // Persister ny toBinCode til DB
              const updateRequest = {
                warehouseID: activeLine.warehouseID || 'W01',
                stcOrderNo: activeLine.stcOrderNo,
                stcLineNo: activeLine.stcLineNo,
                itemNo: activeLine.itemNo,
                quantity: activeLine.quantity,
                fromBinCode: activeLine.fromBinCode,
                toBinCode: newBinCode,
                grShipmentNo: activeLine.grShipmentNo,
                stcBoxNo: activeLine.stcBoxNo,
                lineStatus: activeLine.lineStatus,
                movedBy: activeLine.movedBy,
                itemGrp1: activeLine.itemGrp1,
                itemGrp2: activeLine.itemGrp2,
                itemGrp3: activeLine.itemGrp3,
                description1: activeLine.description1,
                resourceNo: activeLine.resourceNo,
                stcZoneID: activeLine.stcZoneID,
                transType: activeLine.transType,
                clientID: activeLine.clientID,
                quantityMoved: activeLine.quantityMoved,
                itemBarCode: activeLine.itemBarCode,
                externalSystemID: activeLine.externalSystemID,
                externalSystemStatus: activeLine.externalSystemStatus,
                resID: activeLine.resID,
                binGrpNo: activeLine.binGrpNo,
                sorting: activeLine.sorting,
                relID: activeLine.relID,
              };

              await putAwayService.updatePutAwayOrderLine(
                stcOrderNo,
                activeLine.stcLineNo,
                updateRequest
              );

              // Oppdater local state
              setPutAwayLines(prevLines =>
                prevLines.map(line =>
                  line.stcLineNo === activeLine.stcLineNo
                    ? { ...line, toBinCode: newBinCode, location: newBinCode }
                    : line
                )
              );

              // Avanser scan-state til antall (location er bekreftet via opprettelsen)
              const remaining = activeLine.quantity - (activeLine.quantityMoved || 0);
              setScanningState({
                step: 'quantity',
                scannedItem: activeLine.itemNo || null,
                expectedLocation: newBinCode,
                scannedLocation: newBinCode,
                expectedQuantity: remaining,
                tempQuantity: remaining,
                currentLineIndex: activeLineIndex,
              });

              logger.info(
                'ModernPutAwayDetails',
                'New bin auto-selected as destination for active line',
                { newBinCode, stcLineNo: activeLine.stcLineNo, remaining }
              );
            } catch (error) {
              logger.logException('ModernPutAwayDetails', error as Error, {
                operation: 'extendShelf_autoSelectDestination',
                stcOrderNo,
                stcLineNo: activeLine.stcLineNo,
                newBinCode,
              });
            }
          }

          setShowExtendShelfModal(false);
        }}
      />
    </div>
  );
};
